import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { OtpInput } from '@components/OtpInput';
import {
  solicitarOtp,
  validarOtp,
  type MetodoOtp,
  type MetodoOtpInfo,
} from '@lib/services/portal.service';
import { capitalizeName } from '@lib/utils/text';

interface Props {
  token: string;
  documento: string;
  primerNombre: string;
  metodos: MetodoOtpInfo[];
  /**
   * Overrides opcionales para reusar el gate fuera del wizard del titular
   * (Fase 2: activación de legajo_bis, donde el OTP es por token del bis y va
   * al titular del destino). Sin estos props, se comporta como siempre
   * (OTP por `documento` + invalidar la query `['onboarding', token]`).
   */
  onSolicitar?: (metodo: MetodoOtp) => Promise<unknown>;
  onValidar?: (code: string) => Promise<{ ok: boolean }>;
  onValidated?: () => Promise<void> | void;
  /** Copy del encabezado (default: verificación de identidad del titular). */
  subtitulo?: string;
}

type Step = 'metodo' | 'otp';

function extractServerError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string } } } };
  return e.response?.data?.error?.message ?? fallback;
}

/**
 * Como extractServerError pero ademas: si el backend mando un
 * `puedeReintentarA` en los details (caso "demasiados intentos"), lo
 * formatea como hora local del titular ("podes volver a intentar a las
 * 12:34") y lo agrega al mensaje. Asi el titular sabe exactamente cuando
 * va a poder reintentar, sin tener que adivinar.
 */
function extractOtpError(err: unknown, fallback: string): string {
  const e = err as {
    response?: {
      data?: {
        error?: {
          message?: string;
          details?: Array<{ field?: string; message?: string }>;
        };
      };
    };
  };
  const data = e.response?.data?.error;
  const base = data?.message ?? fallback;
  const puedeReintentarRaw = data?.details?.find((d) => d.field === 'puedeReintentarA')?.message;
  if (puedeReintentarRaw) {
    const fecha = new Date(puedeReintentarRaw);
    if (!Number.isNaN(fecha.getTime())) {
      const hora = fecha.toLocaleTimeString('es-AR', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${base} Podés volver a intentar a las ${hora}.`;
    }
  }
  return base;
}

/**
 * Bloqueo OTP previo al wizard: cuando el titular entra al link con un token
 * pero todavía no validó OTP, mostramos esto antes que cualquier otro paso.
 * La validación OTP es la que habilita después la consulta a Renaper (que
 * tiene costo), por eso no puede saltearse.
 */
export function OtpGate({
  token,
  documento,
  primerNombre,
  metodos,
  onSolicitar,
  onValidar,
  onValidated,
  subtitulo,
}: Props) {
  const qc = useQueryClient();
  // Funciones efectivas: por defecto, el flujo del titular (por documento);
  // con overrides, el flujo del bis (por token).
  const doSolicitar = onSolicitar ?? ((m: MetodoOtp) => solicitarOtp(documento, m));
  const doValidar = onValidar ?? ((c: string) => validarOtp(documento, c));
  const afterValidated =
    onValidated ?? (() => qc.invalidateQueries({ queryKey: ['onboarding', token] }));
  const [step, setStep] = useState<Step>('metodo');
  const [metodoSel, setMetodoSel] = useState<MetodoOtp | null>(
    metodos.length > 0 ? metodos[0].tipo : null,
  );
  const [solicitarLoading, setSolicitarLoading] = useState(false);
  const [solicitarError, setSolicitarError] = useState<string | null>(null);

  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpInfo, setOtpInfo] = useState<string | null>(null);

  async function handlePedirCodigo() {
    if (!metodoSel) return;
    setSolicitarError(null);
    setSolicitarLoading(true);
    try {
      await doSolicitar(metodoSel);
      const m = metodos.find((x) => x.tipo === metodoSel);
      setOtpInfo(
        metodoSel === 'sms'
          ? `Te enviamos un SMS con el código a ${m?.mask ?? ''}.`
          : `Te enviamos un email con el código a ${m?.mask ?? ''}.`,
      );
      setStep('otp');
    } catch (err) {
      setSolicitarError(
        extractServerError(err, 'No pudimos enviarte el código. Reintentá.'),
      );
    } finally {
      setSolicitarLoading(false);
    }
  }

  async function handleOtpComplete(code: string) {
    setOtpError(null);
    setOtpLoading(true);
    try {
      const r = await doValidar(code);
      if (r.ok) {
        // OTP OK. Refetcheamos (o lo que indique onValidated) para que el
        // wizard avance: el titular pasa a `en_validacion`; el bis, a activarse.
        await afterValidated();
      }
    } catch (err) {
      setOtpError(extractOtpError(err, 'Código incorrecto.'));
      setOtp('');
    } finally {
      setOtpLoading(false);
    }
  }

  async function handleResend() {
    if (!metodoSel) return;
    setOtpError(null);
    setOtp('');
    setOtpLoading(true);
    try {
      await doSolicitar(metodoSel);
      setOtpInfo('Te enviamos un nuevo código.');
    } catch (err) {
      setOtpError(
        extractServerError(err, 'No pudimos generar un nuevo código.'),
      );
    } finally {
      setOtpLoading(false);
    }
  }

  return (
    <div className="onboarding__form" style={{ maxWidth: 480, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
          color: 'var(--color-primary-darker)',
        }}
      >
        <ShieldCheck size={28} strokeWidth={2} />
        <div>
          <h1 className="heading-3" style={{ margin: 0 }}>
            ¡Hola, {capitalizeName(primerNombre)}!
          </h1>
          <p
            className="text-element-mid"
            style={{ fontSize: 14, margin: 0, marginTop: 2 }}
          >
            {subtitulo ?? 'Antes de seguir necesitamos verificar tu identidad.'}
          </p>
        </div>
      </div>

      {step === 'metodo' && (
        <section className="landing__card" style={{ padding: 0, boxShadow: 'none' }}>
          <p
            className="text-element-mid"
            style={{ fontSize: 14, marginBottom: 12 }}
          >
            ¿Qué método preferís para validar tus datos? Te enviaremos un
            código de 4 dígitos.
          </p>

          {metodos.length === 0 ? (
            <p className="status status-error" style={{ display: 'block' }}>
              No tenemos un email ni un celular registrado para vos. Comunicate
              con el área de RRHH de tu organización para actualizar tus datos.
            </p>
          ) : (
            <fieldset
              style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}
            >
              <legend className="sr-only">Método de validación</legend>
              {metodos.map((m) => {
                const checked = metodoSel === m.tipo;
                return (
                  <label
                    key={m.tipo}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '14px 16px',
                      borderRadius: 'var(--radius-md)',
                      border: `1px solid ${checked ? 'var(--color-primary-darker)' : 'var(--color-outline)'}`,
                      background: checked ? 'rgba(0,37,100,0.04)' : 'var(--color-background)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="metodo-gate"
                      value={m.tipo}
                      checked={checked}
                      onChange={() => setMetodoSel(m.tipo)}
                      disabled={solicitarLoading}
                      style={{ accentColor: 'var(--color-primary-darker)' }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <strong style={{ fontSize: 14 }}>
                        {m.tipo === 'sms' ? 'SMS al celular' : 'Email'}
                      </strong>
                      <span
                        style={{
                          fontSize: 13,
                          color: 'var(--color-element-mid)',
                          fontFamily: 'monospace',
                        }}
                      >
                        {m.mask}
                      </span>
                    </div>
                  </label>
                );
              })}
            </fieldset>
          )}

          {solicitarError && (
            <p
              className="status status-error"
              role="alert"
              style={{ marginTop: 12 }}
            >
              {solicitarError}
            </p>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 22,
            }}
          >
            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePedirCodigo}
              disabled={solicitarLoading || !metodoSel}
              style={{ minWidth: 160 }}
            >
              <span className="btn-font-style">
                {solicitarLoading ? 'Enviando…' : 'Enviarme el código'}
              </span>
            </button>
          </div>
        </section>
      )}

      {step === 'otp' && (
        <section
          className="landing__card"
          style={{ padding: '24px 22px', boxShadow: 'var(--shadow-lg)' }}
        >
          {/* Header centrado estilo medicus.com.ar (image13 del QA 16/05) */}
          <h2
            style={{
              textAlign: 'center',
              fontFamily: 'var(--font-switzer)',
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--color-primary-darker)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              margin: '0 0 18px',
            }}
          >
            Código de verificación
          </h2>

          <div style={{ textAlign: 'center' }}>
            <OtpInput
              length={4}
              value={otp}
              onChange={setOtp}
              onComplete={handleOtpComplete}
              autoFocus
              disabled={otpLoading}
            />
            {otpInfo && !otpError && (
              <p className="text-element-mid" style={{ fontSize: 13, marginTop: 14 }}>
                {otpInfo}
              </p>
            )}
            {otpError && (
              <p
                className="status status-error"
                role="alert"
                style={{ marginTop: 14 }}
              >
                {otpError}
              </p>
            )}
          </div>

          {/* Footer: link a la izquierda "Cambiar método" + link primario a la
              derecha "Reenviar código". Patrón medicus.com.ar. */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 22,
              paddingTop: 14,
              borderTop: '1px solid var(--color-outline-sutil)',
              gap: 12,
            }}
          >
            <button
              type="button"
              onClick={() => {
                setStep('metodo');
                setOtp('');
                setOtpError(null);
                setOtpInfo(null);
              }}
              disabled={otpLoading}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 13,
                color: 'var(--color-element-mid)',
                cursor: 'pointer',
              }}
            >
              ← Cambiar método
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={otpLoading}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 13,
                color: 'var(--color-primary-darker)',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Reenviar código
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

